import React, { useMemo } from "react";
import { useSceneStore } from "../../store/sceneStore";
import { useSimulationStore } from "../../store/simulationStore";
import { getBodyById } from "../../data/catalog";
import { useCelestialPosition } from "../../hooks/useCelestialPosition";
import {
  formatDistanceKm,
  formatMassKg,
  formatSpeedKmS,
  formatUtcDateTime,
} from "../../utils/formatting";
import { vectorLength } from "../../astronomy/kepler";

export function BodyDetailsPanel() {
  const selectedBodyId = useSceneStore((s) => s.selectedBodyId);
  const date = useSimulationStore((s) => s.currentDateUtc);
  const body = selectedBodyId ? getBodyById(selectedBodyId) : undefined;
  const state = useCelestialPosition(selectedBodyId ?? "sun", date);

  const distanceToParent = useMemo(() => {
    if (!state) return 0;
    return vectorLength(state.relativePositionKm);
  }, [state]);

  const speed = useMemo(() => {
    if (!state) return 0;
    return vectorLength(state.velocityKmS);
  }, [state]);

  if (!body || !state) {
    return (
      <aside className="tls-astro-panel">
        <h3>Detalhes</h3>
        <p>Nenhum corpo selecionado.</p>
      </aside>
    );
  }

  const parent = body.parentId ? getBodyById(body.parentId) : null;

  return (
    <aside className="tls-astro-panel">
      <h3>{body.name}</h3>
      <dl className="tls-astro-dl">
        <dt>Tipo</dt>
        <dd>{body.type}</dd>
        <dt>Corpo pai</dt>
        <dd>{parent?.name ?? "—"}</dd>
        <dt>Raio</dt>
        <dd>{formatDistanceKm(body.physical.meanRadiusKm)}</dd>
        <dt>Massa</dt>
        <dd>{formatMassKg(body.physical.massKg)}</dd>
        <dt>Gravidade</dt>
        <dd>
          {body.physical.surfaceGravityMS2 != null
            ? `${body.physical.surfaceGravityMS2} m/s²`
            : "—"}
        </dd>
        <dt>Distância ao pai</dt>
        <dd>{formatDistanceKm(distanceToParent)}</dd>
        <dt>Velocidade orbital</dt>
        <dd>{formatSpeedKmS(speed)}</dd>
        <dt>Período orbital</dt>
        <dd>
          {body.orbit
            ? `${body.orbit.orbitalPeriodDays.toFixed(3)} dias`
            : "—"}
        </dd>
        <dt>Período de rotação</dt>
        <dd>{body.rotation.siderealRotationPeriodHours.toFixed(3)} h</dd>
        <dt>Inclinação orbital</dt>
        <dd>
          {body.orbit ? `${body.orbit.inclinationDeg.toFixed(3)}°` : "—"}
        </dd>
        <dt>Inclinação axial</dt>
        <dd>{body.rotation.axialTiltDeg.toFixed(3)}°</dd>
        <dt>Rotação</dt>
        <dd>{body.rotation.rotationDirection}</dd>
        <dt>Fonte</dt>
        <dd>{state.source}</dd>
        <dt>Coordenadas (km)</dt>
        <dd>
          x {state.absolutePositionKm.x.toExponential(3)}
          <br />
          y {state.absolutePositionKm.y.toExponential(3)}
          <br />
          z {state.absolutePositionKm.z.toExponential(3)}
        </dd>
        <dt>Data da amostra</dt>
        <dd>{formatUtcDateTime(date)}</dd>
      </dl>
    </aside>
  );
}
